import React, { useState, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { Alert, Button, Stack } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import ConfirmedPasswordInput from "../Inputs/ConfirmedPasswordInput";
import { confirmSignIn } from "aws-amplify/auth";
import { useTranslation } from "react-i18next";
import _ from "lodash";

const NewPasswordChallengeForm = ({ setChallengeActive, user }) => {
  const { t } = useTranslation();
  const formController = useForm({ mode: "onChange" });
  const {
    reset,
    handleSubmit,
    formState: { isValid, isDirty, isSubmitting, errors },
  } = formController;

  const [submitError, setSubmitError] = useState(false);
  const [policyAgreed, setPolicyAgreed] = useState(false);

  const returnToSignIn = () => {
    setChallengeActive(false);
  };

  useEffect(() => {
    if (isDirty || isValid) {
      setSubmitError(null);
    }
  }, [isValid, isDirty]);

  const PasswordPolicyInfo =
    "Passwords need to be at least 8 characters long, include a uppercase letter, a lowercase letter, a number and a special character.";

  const validatePassword = (input) => {
    if (input.length < 8) {
      return "Password must be at least 8 characters long.";
    } else if (input.length > 99) {
      return "Password must be less than 99 characters long.";
    } else if (!/[A-Z]/.test(input)) {
      return "Password must include at least one uppercase letter.";
    } else if (!/[a-z]/.test(input)) {
      return "Password must include at least one lowercase letter.";
    } else if (!/[0-9]/.test(input)) {
      return "Password must include at least one number.";
    } else if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(input)) {
      return "Password must include at least one special character.";
    } else {
      return true;
    }
  };

  const completeChallenge = async (data) => {
    try {
      if (validatePassword(data.password) === true) {
        await confirmSignIn({
          challengeResponse: data.password,
        });
        setChallengeActive(false);
      } else {
        console.error(validatePassword(data.password));
        setSubmitError(validatePassword(data.password));
      }
    } catch (error) {
      setSubmitError(error);
      console.error(error);
    }
  };

  const loadingButtonStyle = {
    backgroundColor: "#66cc99",
    color: "#000000",
    "&:hover": {
      backgroundColor: "#37a46e",
    },
  };

  return (
    <>
      <form
        style={{
          display: "flex",
          flexDirection: "column",
        }}
        onSubmit={handleSubmit(completeChallenge)}
      >
        <Stack direction="column" gap={2}>
          <Alert severity="info" sx={{ color: "#000", marginBottom: "0.5rem" }}>
            <span>Please create a new password to complete your sign up.</span>
          </Alert>

          <ConfirmedPasswordInput
            formController={formController}
            submitError={submitError}
            labelNewPassword={t("L1_INPUT_NEW_PASSWORD")}
            labelConfirmNewPw={t("CONFIRM_NEW_PW_INPUT")}
          />

          {submitError && (
            <Alert severity="error">
              <span>{PasswordPolicyInfo}</span>
            </Alert>
          )}

          {/* <TermsAndConditionsCheckbox formController={formController} /> */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              alignContent: "center",
            }}
          >
            <input
              style={{
                webkitAppearance: "auto",
                appearance: "auto",
                backgroundColor: "transparent",
                marginRight: "7px",
                fontSize: "0.75vw",
              }}
              type="checkbox"
              name="privacy-policy"
              value="privacy-policy"
              onClick={() => setPolicyAgreed(!policyAgreed)}
            />
            <label
              style={{
                fontSize: "0.75vw",
                margin: "1em 0",
              }}
              for="privacy-policy"
            >
              {t("OPT_IN_DESCRIPTION")} -{" "}
              <a target="_blank" rel="noreferrer" href={t("PRIVACY_POLICY_URL")}>
                {t("PRIVACY_POLICY_TEXT")}
              </a>
              -{" "}
              <a target="_blank" rel="noreferrer" href={t("IMPRINT_URL")}>
                {t("IMPRINT_TEXT")}
              </a>
            </label>
          </div>

          <LoadingButton
            type="submit"
            loading={isSubmitting}
            disabled={!isValid || !isDirty || !policyAgreed}
            variant="contained"
            sx={loadingButtonStyle}
          >
            create password
          </LoadingButton>
          <Button sx={{ marginTop: "2px", color: "#000" }} size="small" onClick={returnToSignIn}>
            return to sign in
          </Button>
        </Stack>
      </form>
    </>
  );
};

export default NewPasswordChallengeForm;
